import { environment } from '../../environments/environment';
import { isClassicGapHost } from './brand';

/** GAP property Gaponline.PL — Measurement ID G-X32ZVR8W14 in this container. */
export const GAP_GTM_CONTAINER_ID = 'GTM-PNPJMR4';
export const GAP_GA_MEASUREMENT_ID = 'G-X32ZVR8W14';

/** WAR container when hostname is gwarancjeonline. */
export const WAR_GTM_CONTAINER_ID = 'GTM-N3WTG8PM';
export const WAR_GA_MEASUREMENT_ID = 'G-464245693';

/**
 * GTM container for the current host.
 * Shared FORM build must not send GAP sessions to the WAR property (GAP-460).
 * Non-production builds disable GTM entirely.
 */
export function resolveGtmIdFrom(hostname: string, production: boolean): string {
  if (!production) {
    return '';
  }
  return isClassicGapHost(hostname)
    ? GAP_GTM_CONTAINER_ID
    : WAR_GTM_CONTAINER_ID;
}

export function resolveGaIdFrom(hostname: string, production: boolean): string {
  if (!production) {
    return '';
  }
  return isClassicGapHost(hostname)
    ? GAP_GA_MEASUREMENT_ID
    : WAR_GA_MEASUREMENT_ID;
}

export function resolveGaId(
  hostname: string = typeof location !== 'undefined' ? location.hostname : '',
): string {
  return resolveGaIdFrom(hostname, environment.production);
}

export function resolveGtmId(
  hostname: string = typeof location !== 'undefined' ? location.hostname : '',
): string {
  return resolveGtmIdFrom(hostname, environment.production);
}

export const gtmIdProvider = environment.production
  ? [
    {
      provide: 'googleTagManagerId',
      useFactory: () => resolveGtmId(),
    },
  ]
  : [];

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as PromiseLike<unknown>).then === 'function'
  );
}

type GtmDomService =
  | { addGtmToDom: () => void | PromiseLike<unknown> }
  | null
  | undefined;

/**
 * Hard cap so Google Ads still sees the container if the main thread stays
 * busy. Do not wait for Cookiebot — consent defaults already deny ads until
 * the CMP updates them (GAP-500).
 */
export const GTM_IDLE_TIMEOUT_MS = 2500;

export function scheduleGtmLoad(gtmService: GtmDomService): void {
  if (typeof window === 'undefined') {
    return;
  }

  const start = () => loadGtmIfNeeded(gtmService);
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(start, { timeout: GTM_IDLE_TIMEOUT_MS });
    return;
  }

  window.setTimeout(start, GTM_IDLE_TIMEOUT_MS);
}

export function loadGtmIfNeeded(gtmService: GtmDomService): void {
  if (typeof window === 'undefined' || !environment.production) {
    return;
  }
  // Cypress e2e (GAP-e2e-prod / war-prod) hits live prod — do not send hits to GA.
  if ((window as { Cypress?: unknown }).Cypress) {
    return;
  }
  const gtmId = resolveGtmId();
  if (!gtmId || !gtmService) {
    return;
  }
  if (window.__GAP_GTM_LOADED) {
    return;
  }
  // Apex, www, blog and form share the _ga cookie on gaponline.pl.
  // One attempt per document. A blocked host must not insert the script again.
  window.__GAP_GTM_LOADED = true;
  const pending = gtmService.addGtmToDom();
  if (!isThenable(pending)) {
    return;
  }
  // angular-google-tag-manager rejects with `false` when gtm.js errors
  // (adblock or DNS blocking googletagmanager.com). An unhandled boolean
  // rejection becomes Zone's "Uncaught (in promise): [object Boolean]"
  // (Sentry GAPONLINE-4B / GAP-493).
  void Promise.resolve(pending).catch(() => undefined);
}
